import React, { useState } from 'react';
import { CreateRelationshipRequest, relationshipsAPI, PersonDto } from '../services/api';
import { Button, ModalFooter, ModalHeader, Select } from './common';
import { getPersonDisplayName } from '../utils/relationshipLabels';
import { useRelationshipLabels } from '../utils/useRelationshipLabels';
import { trackEvent } from '../services/analytics';
import { useTranslation } from 'react-i18next';

interface AddRelationshipModalProps {
  treeId: string;
  people: PersonDto[];
  currentPersonId?: string;
  initialPersonAId?: string;
  initialPersonBId?: string;
  initialType?: string;
  lockPeopleSelection?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddRelationshipModal: React.FC<AddRelationshipModalProps> = ({
  treeId,
  people,
  currentPersonId,
  initialPersonAId,
  initialPersonBId,
  initialType,
  lockPeopleSelection = false,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { getRelationshipPreview } = useRelationshipLabels();
  const sortedPeople = [...people].sort((left, right) =>
    getPersonDisplayName(left).localeCompare(getPersonDisplayName(right))
  );
  const [formData, setFormData] = useState({
    personAId: initialPersonAId || currentPersonId || '',
    personBId: initialPersonBId || '',
    type: initialType || 'spouse',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const relationshipTypes = [
    { value: 'parent', label: t('relationship.types.parent', { ns: 'canvas' }) },
    { value: 'stepFather', label: t('relationship.types.stepFather', { ns: 'canvas' }) },
    { value: 'stepMother', label: t('relationship.types.stepMother', { ns: 'canvas' }) },
    { value: 'child', label: t('relationship.types.child', { ns: 'canvas' }) },
    { value: 'spouse', label: t('relationship.types.spouse', { ns: 'canvas' }) },
    { value: 'sibling', label: t('relationship.types.sibling', { ns: 'canvas' }) },
    { value: 'stepSibling', label: t('relationship.types.stepSibling', { ns: 'canvas' }) },
    { value: 'aunt', label: t('relationship.types.aunt', { ns: 'canvas' }) },
    { value: 'uncle', label: t('relationship.types.uncle', { ns: 'canvas' }) },
    { value: 'cousin', label: t('relationship.types.cousin', { ns: 'canvas' }) },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate that both people are selected and they're different
      if (!formData.personAId || !formData.personBId) {
        setError(t('validation.selectBothPeople', { ns: 'canvas' }));
        setLoading(false);
        return;
      }

      if (formData.personAId === formData.personBId) {
        setError(t('validation.cannotSamePersonRelationship', { ns: 'canvas' }));
        setLoading(false);
        return;
      }

      const payload: CreateRelationshipRequest = {
        treeId,
        personAId: formData.personAId,
        personBId: formData.personBId,
        type: formData.type,
      };

      await relationshipsAPI.createRelationship(payload);
      trackEvent('relationship_added', { relationship_type: formData.type });
      onSuccess();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail ||
                          err.response?.data?.message ||
                          err.message ||
                          t('relationship.failedToAdd', { ns: 'canvas' });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const personA = formData.personAId ? sortedPeople.find((person) => person.id === formData.personAId) : undefined;
  const personB = formData.personBId ? sortedPeople.find((person) => person.id === formData.personBId) : undefined;

  const personAOptions = sortedPeople.filter((person) => person.id !== formData.personBId);
  const personBOptions = sortedPeople.filter((person) => person.id !== formData.personAId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(26,23,20,0.45)] px-4 py-6"
      onClick={onClose}
      data-testid="add-relationship-backdrop"
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] shadow-[0_24px_60px_rgba(26,23,20,0.24)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-relationship-modal"
      >
        <ModalHeader
          eyebrow={t('relationship.addEyebrow', { ns: 'canvas' })}
          title={t('relationship.addTitle', { ns: 'canvas' })}
          description={t('relationship.addDescription', { ns: 'canvas' })}
          onClose={onClose}
          closeAriaLabel={t('relationship.closeAddAriaLabel', { ns: 'canvas' })}
        />

        {error && (
          <div className="mx-6 mt-5 rounded-xl border border-[#F7C1C1] bg-[#FCEBEB] px-4 py-3 text-sm text-[#A32D2D]">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Person A Selection */}
          <div className="px-6 pt-5">
            <Select
              label={t('relationship.personALabel', { ns: 'canvas' })}
              required
              value={formData.personAId}
              onChange={(e) => setFormData({ ...formData, personAId: e.target.value })}
              disabled={lockPeopleSelection}
              aria-label={t('relationship.personALabel', { ns: 'canvas' })}
              options={personAOptions.map((person) => ({
                value: person.id,
                label: getPersonDisplayName(person),
              }))}
              placeholder={t('relationship.selectPersonPlaceholder', { ns: 'canvas' })}
            />
          </div>

          {/* Relationship Type Selection */}
          <div className="px-6">
            <Select
              label={t('relationship.typeLabel', { ns: 'canvas' })}
              required
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              aria-label={t('relationship.typeLabel', { ns: 'canvas' })}
              data-testid="relationship-type-select"
              options={relationshipTypes}
            />
          </div>

          {/* Person B Selection */}
          <div className="px-6">
            <Select
              label={t('relationship.personBLabel', { ns: 'canvas' })}
              required
              value={formData.personBId}
              onChange={(e) => setFormData({ ...formData, personBId: e.target.value })}
              disabled={lockPeopleSelection}
              aria-label={t('relationship.personBLabel', { ns: 'canvas' })}
              options={personBOptions.map((person) => ({
                value: person.id,
                label: getPersonDisplayName(person),
              }))}
              placeholder={t('relationship.selectPersonPlaceholder', { ns: 'canvas' })}
            />
          </div>

          {/* Summary */}
          {formData.personAId && formData.personBId && (
            <div className="mx-6 rounded-2xl border border-[#CFE0F3] bg-[#EEF4FB] px-4 py-4 text-sm font-medium text-[#185FA5]">
              {getRelationshipPreview(personA, formData.type, personB)}
            </div>
          )}

          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="sm:min-w-[160px]"
            >
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.personAId || !formData.personBId}
              data-testid="relationship-save-button"
              className="sm:min-w-[220px]"
            >
              {loading ? t('relationship.savingLabel', { ns: 'canvas' }) : t('relationship.saveLabel', { ns: 'canvas' })}
            </Button>
          </ModalFooter>
        </form>
      </div>
    </div>
  );
};
